<template>
  <div class="game-container">
    <div class="header">
      <h1>📊 Data Engineering Empire</h1>
      <p>Start with raw data, build the ultimate data platform!</p>
    </div>
    <GameControls />
    <GameStats />
    <EventDisplay />
    <Tabs />
    <GameLog />
    <TeamStats />
    <VersionDisplay />

    <footer class="author-footer">
      <span>Made by <a href="https://luminousmen.com" target="_blank" rel="noopener">Luminousmen</a></span>
      <span class="footer-sep">·</span>
      <a href="https://luminousmen.substack.com/welcome" target="_blank" rel="noopener">Subscribe</a>
      <span class="footer-sep">·</span>
      <a href="https://www.linkedin.com/in/luminousmen/" target="_blank" rel="noopener">LinkedIn</a>
      <span class="footer-sep">·</span>
      <a href="https://forms.gle/ihvMnicqL4XELw6d8" target="_blank" rel="noopener">🐛 Report Bug</a>
    </footer>

    <!-- End Game Modal -->
    <div v-if="store.showEndGameModal" class="modal-overlay" @click="store.closeEndGameModal">
      <div class="modal-content" @click.stop>
        <h2>{{ store.endGameResult === 'won' ? '🎉 VICTORY!' : '💥 GAME OVER!' }}</h2>
        <p v-if="store.endGameResult === 'won'" class="victory-message">🤖 The answer is 42</p>
        <div class="modal-stats">
          <h3>📊 Lifetime Stats</h3>
          <p>💰 Max Money: ${{ formatNumber(store.state.lifetimeStats.maxMoney) }}</p>
          <p>📦 Raw Data Collected: {{ formatNumber(store.state.lifetimeStats.totalRawDataCollected) }}</p>
          <p>🧹 Clean Data Produced: {{ formatNumber(store.state.lifetimeStats.totalCleanDataProduced) }}</p>
          <p>🔧 Features Engineered: {{ formatNumber(store.state.lifetimeStats.totalFeaturesEngineered) }}</p>
          <p>🤖 Models Built: {{ formatNumber(store.state.lifetimeStats.totalModelsBuilt) }}</p>
          <p>🏗️ Infrastructure Built: {{ store.state.lifetimeStats.totalInfrastructureBuilt }}</p>
          <p>👥 Personnel Hired: {{ store.state.lifetimeStats.totalPersonnelHired }}</p>
          <p v-if="store.endGameResult === 'won'">🎯 AGI Achievement: 100%</p>
          <p v-else>🤖 AGI Progress: {{ Math.floor(store.state.agiProgress) }}%</p>
          <p>🏆 Achievements: {{ store.state.achievements.length }}</p>
        </div>

        <div class="share-section">
          <h3>Share Your Achievement! 🚀</h3>
          <div class="share-buttons">
            <button class="share-btn twitter" @click="handleTwitterShare" title="Share on Twitter/X">
              <span class="share-icon">𝕏</span>
              <span class="share-label">Twitter</span>
            </button>
            <button class="share-btn linkedin" @click="handleLinkedInShare" title="Share on LinkedIn">
              <span class="share-icon">in</span>
              <span class="share-label">LinkedIn</span>
            </button>
            <button class="share-btn facebook" @click="handleFacebookShare" title="Share on Facebook">
              <span class="share-icon">f</span>
              <span class="share-label">Facebook</span>
            </button>
            <button class="share-btn instagram" @click="handleInstagramShare" title="Copy for Instagram">
              <span class="share-icon">📷</span>
              <span class="share-label">Instagram</span>
            </button>
          </div>
          <div v-if="shareStatus" class="share-status">{{ shareStatus }}</div>
        </div>

        <div class="subscribe-cta">
          <p class="cta-text">Want more data engineering content?<br>Pipelines, distributed systems, career moves — the real stuff.</p>
          <a href="https://luminousmen.substack.com/welcome" target="_blank" rel="noopener" class="cta-btn">Subscribe on Substack</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useGameStore } from '@/stores/gameStore';
import {
  formatNumber,
  getTwitterShareText,
  getLinkedInShareText,
  getFacebookShareText,
  getInstagramShareText,
  shareToTwitter,
  shareToLinkedIn,
  shareToFacebook,
  copyToClipboard
} from '@/utils/gameUtils';
import GameStats from '@/components/GameStats.vue';
import TeamStats from '@/components/TeamStats.vue';
import GameLog from '@/components/GameLog.vue';
import EventDisplay from '@/components/EventDisplay.vue';
import GameControls from '@/components/GameControls.vue';
import Tabs from '@/components/Tabs.vue';
import VersionDisplay from '@/components/VersionDisplay.vue';

const store = useGameStore();
const shareStatus = ref('');

const handleTwitterShare = () => {
  const text = getTwitterShareText(store.state);
  shareToTwitter(text);
  shareStatus.value = 'Opening Twitter...';
  setTimeout(() => shareStatus.value = '', 2000);
};

const handleLinkedInShare = () => {
  const text = getLinkedInShareText(store.state);
  shareToLinkedIn(text);
  shareStatus.value = 'Opening LinkedIn...';
  setTimeout(() => shareStatus.value = '', 2000);
};

const handleFacebookShare = () => {
  const text = getFacebookShareText(store.state);
  shareToFacebook('https://game.luminousmen.com/', text);
  shareStatus.value = 'Opening Facebook...';
  setTimeout(() => shareStatus.value = '', 2000);
};

const handleInstagramShare = async () => {
  const text = getInstagramShareText(store.state);
  const success = await copyToClipboard(text);
  shareStatus.value = success
    ? '✅ Copied to clipboard! Paste in Instagram caption'
    : '❌ Failed to copy';
  setTimeout(() => shareStatus.value = '', 3000);
};

onMounted(() => {
  store.load();
  store.start();
});
</script>

<style scoped>
.game-container {
  max-width: 1400px;
  margin: 0 auto;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
}

.header {
  text-align: center;
  margin-bottom: 30px;
  border-bottom: 2px solid #00ff88;
  padding-bottom: 20px;
}

.header h1 {
  color: #00ff88;
  font-size: 2.5em;
  text-shadow: 0 0 10px #00ff88;
}

.game-controls {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 20px;
}

.control-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}

.control-btn.restart {
  background: linear-gradient(45deg, #ffaa00, #ff8800);
  color: #000;
}

.control-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 170, 0, 0.3);
}

.events {
  background: rgba(255, 170, 0, 0.1);
  border: 1px solid #ffaa00;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  text-align: center;
  display: none;
}

.events:not(:empty) {
  display: block;
}

.event-text {
  color: #ffaa00;
  font-weight: bold;
}

.tabs {
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #444;
}

.tab {
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-bottom: none;
  cursor: pointer;
  margin-right: 5px;
  border-radius: 5px 5px 0 0;
  transition: all 0.3s ease;
}

.tab:hover {
  background: rgba(255, 255, 255, 0.1);
}

.tab.active {
  background: rgba(0, 255, 136, 0.2);
  border-color: #00ff88;
}

.tab-content {
  margin-bottom: 20px;
}

.pipeline {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.upgrades {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
}

.achievements h3 {
  color: #00ff88;
  margin-bottom: 15px;
}

.achievement {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 8px;
  color: #00ff88;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.modal-content {
  background: #222;
  color: #fff;
  border-radius: 12px;
  max-width: 420px;
  max-height: 90vh;
  margin: auto;
  padding: 20px 18px;
  text-align: center;
  box-shadow: 0 0 32px #000;
  overflow-y: auto;
}

.modal-content h2 {
  color: #00ff88;
  margin-bottom: 12px;
}

.victory-message {
  color: #00ff88;
  font-size: 1.4em;
  font-weight: bold;
  margin: 10px 0;
  padding: 12px 20px;
  text-align: center;
  text-shadow: 0 0 20px rgba(0, 255, 136, 0.8);
  background: rgba(0, 255, 136, 0.1);
  border: 3px solid #00ff88;
  border-radius: 12px;
  box-shadow: 0 0 30px rgba(0, 255, 136, 0.4), inset 0 0 20px rgba(0, 255, 136, 0.1);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 30px rgba(0, 255, 136, 0.4), inset 0 0 20px rgba(0, 255, 136, 0.1);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 40px rgba(0, 255, 136, 0.6), inset 0 0 30px rgba(0, 255, 136, 0.15);
  }
}

.modal-stats {
  margin: 10px 0 8px 0;
  text-align: left;
}

.modal-stats h3 {
  color: #00ff88;
  margin-bottom: 12px;
  text-align: center;
  font-size: 1.2em;
}

.modal-stats p {
  margin: 4px 0;
  font-size: 0.85em;
}

.share-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #444;
}

.share-section h3 {
  color: #00ff88;
  margin-bottom: 8px;
  text-align: center;
  font-size: 0.95em;
}

.share-buttons {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  justify-content: center;
}

.share-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.share-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.share-btn:hover::before {
  opacity: 1;
}

.share-icon {
  font-size: 1em;
  font-weight: bold;
}

.share-label {
  font-size: 0.75em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.share-btn.twitter {
  background: linear-gradient(135deg, #1DA1F2 0%, #0d8bd9 100%);
  color: white;
}

.share-btn.twitter:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(29, 161, 242, 0.4);
}

.share-btn.linkedin {
  background: linear-gradient(135deg, #0077B5 0%, #005582 100%);
  color: white;
}

.share-btn.linkedin .share-icon {
  font-size: 1em;
  font-family: Arial, sans-serif;
  font-weight: bold;
}

.share-btn.linkedin:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 119, 181, 0.4);
}

.share-btn.facebook {
  background: linear-gradient(135deg, #1877F2 0%, #0d5dbf 100%);
  color: white;
}

.share-btn.facebook .share-icon {
  font-size: 1em;
  font-family: Arial, sans-serif;
  font-weight: bold;
}

.share-btn.facebook:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(24, 119, 242, 0.4);
}

.share-btn.instagram {
  background: linear-gradient(135deg, #E1306C 0%, #C13584 50%, #833AB4 100%);
  color: white;
}

.share-btn.instagram:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(225, 48, 108, 0.4);
}

.share-status {
  margin-top: 12px;
  padding: 8px;
  color: #00ff88;
  text-align: center;
  font-size: 0.9em;
  background: rgba(0, 255, 136, 0.1);
  border-radius: 6px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.subscribe-cta {
  margin-top: 12px;
  padding: 12px;
  border-top: 1px solid #444;
  text-align: center;
}

.cta-text {
  color: #ccc;
  font-size: 0.8em;
  line-height: 1.4;
  margin-bottom: 10px;
}

.cta-btn {
  display: inline-block;
  padding: 8px 20px;
  background: linear-gradient(135deg, #ff6719 0%, #e05510 100%);
  color: #fff;
  font-family: 'Courier New', monospace;
  font-size: 0.95em;
  font-weight: bold;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 103, 25, 0.4);
}

.author-footer {
  text-align: center;
  padding: 20px 0 8px;
  margin-top: 24px;
  border-top: 1px solid #333;
  font-size: 0.85em;
  color: #888;
}

.author-footer a {
  color: #00ff88;
  text-decoration: none;
  transition: color 0.2s ease;
}

.author-footer a:hover {
  color: #fff;
  text-shadow: 0 0 6px #00ff88;
}

.footer-sep {
  margin: 0 8px;
  color: #444;
}

@media (max-width: 768px) {
  .pipeline {
    grid-template-columns: 1fr;
  }

  .upgrades {
    grid-template-columns: 1fr;
  }

  .modal-content {
    max-width: 90vw;
    padding: 24px 16px;
  }

  .share-buttons {
    grid-template-columns: 1fr;
  }

  .modal-stats p {
    font-size: 0.9em;
  }
}
</style> 